import { useEffect, useState } from 'react'
import { Box, Button, Card, CardBody, CardHeader, Heading, Spinner, Text } from '@chakra-ui/react'
import { useNavigate, useParams } from 'react-router-dom'
import { ParentTicketForm } from '@/components/forms/ParentTicketForm'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useParentTicketStore } from '@/store/parentTicket'
import { TicketService } from '@/lib/services/ticket'

// React Strict Mode re-runs mount effects in local development. Sharing an
// in-flight detail request prevents a page reload from fetching the same full
// ticket payload twice at the same time.
const inFlightTicketLoads = new Map<string, ReturnType<typeof TicketService.getParentTicket>>()

function loadParentTicketOnce(id: string) {
  const existing = inFlightTicketLoads.get(id)
  if (existing) return existing

  const request = TicketService.getParentTicket(id)
  inFlightTicketLoads.set(id, request)
  void request.then(
    () => inFlightTicketLoads.delete(id),
    () => inFlightTicketLoads.delete(id),
  )

  return request
}

export default function ParentTicketEditPage() {
  const colors = useThemeColors()
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const mergeTicket = useParentTicketStore((s) => s.mergeTicket)
  const ticket = useParentTicketStore((s) =>
    id ? s.tickets.find((t) => String(t.id) === String(id)) : undefined,
  )
  const [fetchError, setFetchError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(Boolean(id))
  const showFullPageLoader = isLoading && fetchError == null && Boolean(id)

  useEffect(() => {
    if (!id) {
      navigate('/tickets', { replace: true })
    }
  }, [id, navigate])

  useEffect(() => {
    if (!id) return

    let cancelled = false
    setFetchError(null)
    setIsLoading(true)

    loadParentTicketOnce(id)
      .then((res) => {
        if (cancelled) return
        if (res.success && res.data) {
          mergeTicket(res.data)
        } else {
          setFetchError(res.message ?? 'Failed to load ticket')
        }
      })
      .catch(() => {
        if (!cancelled) setFetchError('Failed to load ticket')
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [id, mergeTicket])

  const handleSuccess = (ticketId: string) => {
    if (ticketId) {
      navigate(`/tickets/${ticketId}/view`)
    } else {
      navigate('/tickets')
    }
  }

  const handleCancel = () => {
    navigate('/tickets')
  }

  if (!id) {
    return null
  }

  if (showFullPageLoader) {
    return (
      <Box p={4} display="flex" alignItems="center" justifyContent="center" minH="200px">
        <Spinner size="lg" />
        <Text ml={3}>Loading ticket...</Text>
      </Box>
    )
  }

  if (fetchError) {
    return (
      <Box p={4}>
        <Card>
          <CardBody>
            <Text color="gray.600">{fetchError}</Text>
            <Button variant="link" colorScheme="blue" mt={2} onClick={() => navigate('/tickets')}>
              Back to list
            </Button>
          </CardBody>
        </Card>
      </Box>
    )
  }

  if (!ticket) {
    return (
      <Box p={4}>
        <Card>
          <CardBody>
            <Text color="gray.600">Ticket not found.</Text>
            <Button variant="link" colorScheme="blue" mt={2} onClick={() => navigate('/tickets')}>
              Back to list
            </Button>
          </CardBody>
        </Card>
      </Box>
    )
  }

  return (
    <Box p={4} h="full" overflow="hidden" display="flex" flexDirection="column">
      <Box minW={0} display="flex" flexDirection="column" overflow="hidden" flex="1">
        <Card h="full" display="flex" flexDirection="column">
          <CardHeader flexShrink={0}>
            <Heading size="md">Edit Ticket — {ticket.ticketNumber ?? id}</Heading>
            <Text fontSize="sm" color={colors.text} mt={1}>
              Update ticket details and assignment
            </Text>
          </CardHeader>
          <CardBody overflowY="auto" flex="1" minH={0} px={{ base: 4, md: 5 }} py={4}>
            <ParentTicketForm
              parentTicketId={id}
              onSuccess={handleSuccess}
              onCancel={handleCancel}
            />
          </CardBody>
        </Card>
      </Box>
    </Box>
  )
}
